import apiClient from "./apiClient";
import { AxiosError } from "axios";

/**
 * Interface for Plan from backend
 */
export interface Plan {
  id: string;
  name: string;
  description: string;
  price: number;
  currency: string;
  billingInterval?: "month" | "year"; // Intervalle de facturation
  discountPercentage?: number | null; // Pourcentage de réduction (0-100)
  features: string[];
  enabledFeatures?: string[]; // Fonctionnalités activées pour ce plan
  maxUsers?: number | null;
  isActive: boolean;
  isPopular: boolean;
  badge?: string;
  badgeColor?: string;
  displayOrder: number;
  productId?: string;
  priceId?: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Interface for Subscription creation
 */
export interface CreateSubscriptionRequest {
  planId: string;
  paymentMethod?: string;
  stripeCustomerId?: string;
}

/**
 * Interface for user's subscription
 */
export interface UserSubscription {
  id: string;
  userId: string;
  planId: string | null;
  status:
    | "active"
    | "inactive"
    | "pending"
    | "canceled"
    | "expired"
    | "trialing";
  startDate: string | null;
  endDate: string | null;
  cancelledAt?: string | null;
  paymentMethod?: string | null;
  stripeSubscriptionId?: string;
  stripeCustomerId?: string;
  autoRenew: boolean;
  cancelAtPeriodEnd?: boolean; // Indicates if subscription will cancel at period end
  trialStart?: string | null; // Trial period start date
  trialEnd?: string | null; // Trial period end date
  metadata?: Record<string, string | number | boolean>;
  createdAt: string;
  updatedAt: string;
  plan: Plan | null;
}

/**
 * Plans service for handling public plan operations
 */

const plansService = {
  /**
   * Get all active plans (public access)
   */
  getPlans: async (): Promise<Plan[]> => {
    try {
      const response = await apiClient.get("/api/plans");
      return response.data.data;
    } catch (error: unknown) {
      if (error instanceof AxiosError && error.response) {
        throw new Error(error.response.data.message || "Failed to fetch plans");
      }
      throw new Error("An unexpected error occurred while fetching plans");
    }
  },

  /**
   * Get user's current subscription
   */
  getUserSubscription: async (): Promise<UserSubscription | null> => {
    try {
      const response = await apiClient.get("/api/subscription");
      return response.data.data;
    } catch (error: unknown) {
      if (error instanceof AxiosError && error.response) {
        if (error.response.status === 404) {
          return null; // No active subscription
        }
        throw new Error(
          error.response.data.message || "Failed to fetch subscription",
        );
      }
      throw new Error(
        "An unexpected error occurred while fetching subscription",
      );
    }
  },

  /**
   * Create a new subscription
   */
  createSubscription: async (
    data: CreateSubscriptionRequest,
  ): Promise<UserSubscription> => {
    try {
      const response = await apiClient.post("/api/subscription", data);
      return response.data.data;
    } catch (error: unknown) {
      if (error instanceof AxiosError && error.response) {
        throw new Error(
          error.response.data.message || "Failed to create subscription",
        );
      }
      throw new Error(
        "An unexpected error occurred while creating subscription",
      );
    }
  },

  /**
   * Cancel user's subscription
   */
  cancelSubscription: async (): Promise<{
    success: boolean;
    message: string;
    data: {
      subscriptionId: string;
      status: string;
      cancelAtPeriodEnd?: boolean;
      currentPeriodEnd?: Date | null;
      canceledAt?: Date | null;
      effectiveStatus?: string;
    };
  }> => {
    try {
      const response = await apiClient.put("/api/subscription/cancel");
      return response.data;
    } catch (error: unknown) {
      if (error instanceof AxiosError && error.response) {
        throw new Error(
          error.response.data.message || "Failed to cancel subscription",
        );
      }
      throw new Error(
        "An unexpected error occurred while canceling subscription",
      );
    }
  },

  /**
   * Reactivate user's subscription
   */
  reactivateSubscription: async (): Promise<{
    success: boolean;
    message: string;
    data: {
      subscriptionId: string;
      status: string;
      cancelAtPeriodEnd?: boolean;
      currentPeriodEnd?: Date | null;
      effectiveStatus?: string;
    };
  }> => {
    try {
      const response = await apiClient.put("/api/subscription/reactivate");
      return response.data;
    } catch (error: unknown) {
      if (error instanceof AxiosError && error.response) {
        throw new Error(
          error.response.data.message || "Failed to reactivate subscription",
        );
      }
      throw new Error(
        "An unexpected error occurred while reactivating subscription",
      );
    }
  },
};

export default plansService;
